import { action, extendObservable } from 'mobx';
import PropTypes from 'prop-types';
import FlagStore from '../FlagStore';
import InputStore from '../InputStore';
import DatepickerStore from '../DatepickerStore';
import { apiCall } from '../../API';
import AsyncActionStore from '../AsyncActionStore';
import MenuStore from '../MenuStore';

export default class AccessCodesEditStore {
    static MAX_ACTIVATION_LIMIT = 10000;

    static MODAL_TYPE = {
        ADD: 'ADD',
        EDIT: 'EDIT',
        DEACTIVATE: 'DEACTIVATE',
        DELETE: 'DELETE',
    };

    static PropType = PropTypes.shape({
        modalType: PropTypes.string.isRequired,
        modal: FlagStore.PropType.isRequired,
        policyName: PropTypes.string.isRequired,
        neverExpire: FlagStore.PropType.isRequired,
        isUnlimited: FlagStore.PropType.isRequired,
        activationsLimit: InputStore.PropType.isRequired,
        isPreventNewActions: FlagStore.PropType.isRequired,
        confirmAction: FlagStore.PropType.isRequired,
        setAccessCode: PropTypes.func.isRequired,
        createStatus: AsyncActionStore.PropType.isRequired,
        updateStatus: AsyncActionStore.PropType.isRequired,
        menu: MenuStore.PropType.isRequired,
        hasChanged: PropTypes.func.isRequired,
    });

    constructor(data = {}) {
        this.policy = null;
        this.accessCodeObj = null;
        extendObservable(this, /** @class AccessCodesEditStore */{
            menu: new MenuStore(),
            isModalOpen: new FlagStore(),
            modalType: null,
            policyName: '',
            accessCode: '',
            activations: '',
            confirmAction: new FlagStore(false),
            neverExpire: new FlagStore(false),
            datepickerStore: new DatepickerStore(),
            isUnlimited: new FlagStore(false),
            activationsLimit: new InputStore({ value: '' }),
            isPreventNewActions: new FlagStore(false),
            deactivated: new FlagStore(false),
            apiUpdate: apiCall('/console/policy_access_code/update'),
            updateStatus: new AsyncActionStore(),
            apiCreate: apiCall('/console/policy_access_code/create'),
            createStatus: new AsyncActionStore(),
            apiDelete: apiCall('/console/policy_access_code/delete'),
            deleteStatus: new AsyncActionStore(),
        }, data);

        this.activationsLimit.setValidator((value) => {
            if (this.isUnlimited.isTrue) {
                return true;
            }
            const intValue = Number(value);
            const errorMsg = `Enter a value of 1-${AccessCodesEditStore.MAX_ACTIVATION_LIMIT}`;
            if ((this.activationsLimit.isEmpty && this.menu.isOpen())
                || !/^\d+$/i.test(value)
                || intValue > AccessCodesEditStore.MAX_ACTIVATION_LIMIT) {
                return errorMsg;
            }

            if (intValue <= Number(this.activations)) {
                return 'Activation limit cannot be less than current activations.';
            }
            return true;
        });
    }

    isDeleteModalOpen = () => this.isModalOpen.isOpen
        && this.modalType === AccessCodesEditStore.MODAL_TYPE.DELETE;

    isDeactivateModalOpen = () => this.isModalOpen.isOpen
        && this.modalType === AccessCodesEditStore.MODAL_TYPE.DEACTIVATE;

    isActivationsFieldDisabled = () => {
        return this.isUnlimited.value ||
            (this.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT && this.isPreventNewActions.value);
    };

    showCreate = action((policy) => {
        this.cleanUpModal();
        this.modalType = AccessCodesEditStore.MODAL_TYPE.ADD;
        this.policy = policy;
        this.policyName = policy.name;
        this.neverExpire.setTrue();
        this.isUnlimited.setTrue();
        this.isPreventNewActions.setFalse();
        this.datepickerStore.setDefaultDate(this.datepickerStore.getGmtDate().add(1, 'day'));
        this.activationsLimit.setValue('10');
        this.isModalOpen.open();
    });

    showEdit = action((policy, accessCode) => {
        this.cleanUpModal();
        this.menu.close();
        this.modalType = AccessCodesEditStore.MODAL_TYPE.EDIT;
        this.policy = policy;
        this.policyName = policy.name;
        this.setAccessCode(accessCode);
        this.isModalOpen.open();
    });

    showDelete = action((policy, accessCode) => {
        this.cleanUpModal();
        this.menu.close();
        this.modalType = AccessCodesEditStore.MODAL_TYPE.DELETE;
        this.policy = policy;
        this.policyName = policy.name;
        this.setAccessCode(accessCode);
        this.isModalOpen.open();
    });

    showDeactivate = action((policy, accessCode) => {
        this.cleanUpModal();
        this.menu.close();
        this.modalType = AccessCodesEditStore.MODAL_TYPE.DEACTIVATE;
        this.policy = policy;
        this.policyName = policy.name;
        this.setAccessCode(accessCode);
        this.isModalOpen.open();
    });

    onAccessCodeSubmit = action(() => {
        if (this.modalType === AccessCodesEditStore.MODAL_TYPE.EDIT) {
            return this.updateAccessCode();
        }
        if (this.modalType === AccessCodesEditStore.MODAL_TYPE.ADD) {
            return this.createAccessCode();
        }
        if (this.modalType === AccessCodesEditStore.MODAL_TYPE.DELETE) {
            return this.deleteAccessCode();
        }
        if (this.modalType === AccessCodesEditStore.MODAL_TYPE.DEACTIVATE) {
            this.deactivated.setTrue();
            return this.updateAccessCode();
        }
    });

    closeModal = action(() => {
        this.datepickerStore.closeCalendar();
        this.isModalOpen.close();
    });

    cleanUpModal = () => {
        this.modalType = null;
        this.policy = null;
        this.accessCodeObj = null;
        this.policyName = '';
        this.accessCode = '';
        this.activations = '';
        this.deactivated.setFalse();
        this.confirmAction.setFalse();
        this.neverExpire.setFalse();
        this.isUnlimited.setFalse();
        this.isPreventNewActions.setFalse();
        this.activationsLimit.setValue('');
    };

    deleteAccessCode = () => {
        if (!this.deleteStatus.isLoading) {
            const accessCodeDTO = this.value();
            this.deleteStatus.setLoading(this.apiDelete(accessCodeDTO)
                .then(() => this.accessCodeObj));
        }
        return this.deleteStatus.promise;
    };

    createAccessCode = () => {
        if (!this.createStatus.isLoading) {
            const accessCodeDTO = this.value();
            this.createStatus.setLoading(this.apiCreate(accessCodeDTO));
        }
        return this.createStatus.promise;
    };

    updateAccessCode = () => {
        if (!this.updateStatus.isLoading) {
            const accessCodeDTO = this.value();
            this.updateStatus.setLoading(this.apiUpdate(accessCodeDTO));
        }
        return this.updateStatus.promise;
    };

    setAccessCode = (accessCode) => {
        this.accessCodeObj = accessCode;
        this.accessCode = accessCode.code;
        this.activations = accessCode.activations;
        this.activationsLimit.setValue(accessCode.activationsAllowed);
        this.datepickerStore.setStringDate(accessCode.expirationDate);
        this.neverExpire.setValue(accessCode.neverExpire);
        this.isUnlimited.setValue(accessCode.unlimitedActivations);
        this.isPreventNewActions.setValue(accessCode.preventNewActivations);
        this.deactivated.setValue(accessCode.deactivated);
    };

    value = () => ({
        id: this.accessCodeObj ? this.accessCodeObj.id : '',
        policyName: this.policyName,
        code: this.accessCode,
        policyId: this.policy ? this.policy.id : null,
        activationsAllowed: this.activationsLimit.value,
        activations: this.activations,
        expirationDate: this.datepickerStore.date,
        neverExpire: this.neverExpire.value,
        unlimitedActivations: this.isUnlimited.value,
        deactivated: this.deactivated.value,
        preventNewActivations: this.isPreventNewActions.value,
    });

    hasChanged = () => {
        return !this.neverExpire.equals(this.accessCodeObj.neverExpire)
            || !this.isUnlimited.equals(this.accessCodeObj.unlimitedActivations)
            || !this.activationsLimit.equals(this.accessCodeObj.activationsAllowed)
            || !this.datepickerStore.equals(this.accessCodeObj.expirationDate);
    };

    isValid = () => this.activationsLimit.isValid;
}
